import {useEffect, useMemo, useState} from 'react';
import {Layout} from '@/components/Layout';
import {Button} from '@/components/ui/button';
import {Card} from '@/components/ui/card';
import {Badge} from '@/components/ui/badge';
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog';
import {Input} from '@/components/ui/input';
import {Label} from '@/components/ui/label';
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import {Textarea} from '@/components/ui/textarea';
import {toast} from 'sonner';
import {Edit2, Palette, Plus, Search, Shapes, Trash2, UserCog} from 'lucide-react';
import {usePermissions} from '@/hooks/usePermissions';
import {
    api,
    externalContactTypeEndpoints,
    getApiErrorMessage,
    objectColorEndpoints,
    objectIconEndpoints,
} from '@/lib/api';
import {PERMISSIONS} from '@/lib/permissions';

const emptyForm = {
    code: '',
    name: '',
    description: '',
    object_color_id: '',
    object_icon_id: '',
    status: '1',
};

const collectionPayload = (payload, key) => {
    const source = payload?.data ?? payload;
    if (Array.isArray(source)) return source;
    if (Array.isArray(source?.data)) return source.data;
    if (Array.isArray(source?.[key])) return source[key];
    return [];
};

const idFrom = (source) => source?.id ?? source?.object_color_id ?? source?.object_icon_id ?? '';

const normalizeOption = (source = {}) => {
    const id = idFrom(source);
    const label = source.label || source.name || source.title || String(id);
    return {
        ...source,
        id,
        name: label,
        label,
        color: source.color || source.hex || source.hex_color || source.value || '',
        icon: source.icon || source.icon_name || source.name || '',
    };
};

const ColorOption = ({color}) => (
    <span className="inline-flex items-center gap-2 min-w-0">
        <span
            className="w-3 h-3 rounded-full border border-slate-200 flex-shrink-0"
            style={{backgroundColor: color?.color || '#64748b'}}
        />
        <span className="truncate">{color?.label || color?.name || 'Sem nome'}</span>
    </span>
);

const IconOption = ({icon}) => (
    <span className="inline-flex items-center gap-2 min-w-0">
        {icon?.icon ? (
            <img src={icon.icon} alt="" className="w-4 h-4 object-contain flex-shrink-0"/>
        ) : (
            <Shapes size={14} className="text-slate-500 flex-shrink-0"/>
        )}
        <span className="truncate">{icon?.label || icon?.name || 'Sem nome'}</span>
    </span>
);

const normalizeType = (source = {}, fallback = {}) => ({
    ...fallback,
    ...source,
    id: source.id ?? source.external_contact_type_id ?? fallback.id,
    uuid: source.uuid ?? fallback.uuid,
    code: source.code ?? fallback.code ?? '',
    name: source.name ?? fallback.name ?? '',
    description: source.description ?? fallback.description ?? '',
    object_color_id: source.object_color_id ?? source.objectColorId ?? fallback.object_color_id ?? null,
    object_icon_id: source.object_icon_id ?? source.objectIconId ?? fallback.object_icon_id ?? null,
    status: Number(source.status ?? fallback.status ?? 0),
});

const normalizeTypes = (payload) =>
    collectionPayload(payload, 'contact_types').map((item, index) => normalizeType(item, {id: index + 1}));

const typePayload = (form) => ({
    code: form.code.trim(),
    name: form.name.trim(),
    description: form.description.trim() || null,
    object_color_id: form.object_color_id ? Number(form.object_color_id) : null,
    object_icon_id: form.object_icon_id ? Number(form.object_icon_id) : null,
    status: Number(form.status),
});

const SETTINGS_ACTION_PERMISSIONS = {
    create: PERMISSIONS.SETTINGS_CREATE,
    read: PERMISSIONS.SETTINGS_READ,
    update: PERMISSIONS.SETTINGS_UPDATE,
    delete: PERMISSIONS.SETTINGS_DELETE,
};

export const ExternalContactTypesPage = () => {
    const {can} = usePermissions();
    const [types, setTypes] = useState([]);
    const [colors, setColors] = useState([]);
    const [icons, setIcons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingType, setEditingType] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [submitting, setSubmitting] = useState(false);

    const canReadSettings = can(SETTINGS_ACTION_PERMISSIONS.read);
    const canCreateSettings = can(SETTINGS_ACTION_PERMISSIONS.create);
    const canUpdateSettings = can(SETTINGS_ACTION_PERMISSIONS.update);
    const canDeleteSettings = can(SETTINGS_ACTION_PERMISSIONS.delete);
    const showActions = canUpdateSettings || canDeleteSettings;

    useEffect(() => {
        let cancelled = false;

        const loadData = async () => {
            if (!canReadSettings) {
                setTypes([]);
                setColors([]);
                setIcons([]);
                setError('Nao tem permissão para consultar os tipos de contactos.');
                setLoading(false);
                return;
            }

            setLoading(true);
            setError('');

            try {
                const [typesResponse, colorsResponse, iconsResponse] = await Promise.all([
                    api.get(externalContactTypeEndpoints.list),
                    api.get(objectColorEndpoints.list).catch(() => ({data: []})),
                    api.get(objectIconEndpoints.list).catch(() => ({data: []})),
                ]);

                if (!cancelled) {
                    setTypes(normalizeTypes(typesResponse.data));
                    setColors(collectionPayload(colorsResponse.data, 'colors').map(normalizeOption).filter(option => option.id));
                    setIcons(collectionPayload(iconsResponse.data, 'icons').map(normalizeOption).filter(option => option.id));
                }
            } catch (requestError) {
                if (!cancelled) {
                    setTypes([]);
                    setError(getApiErrorMessage(requestError, 'Não foi possível carregar os tipos de contactos.'));
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadData();

        return () => {
            cancelled = true;
        };
    }, [canReadSettings]);

    const colorById = useMemo(() => new Map(colors.map(color => [String(color.id), color])), [colors]);
    const iconById = useMemo(() => new Map(icons.map(icon => [String(icon.id), icon])), [icons]);

    const filteredTypes = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return types;
        return types.filter(type => [type.code, type.name, type.description, type.uuid].filter(Boolean).some(value => String(value).toLowerCase().includes(q)));
    }, [types, search]);

    const openCreate = () => {
        if (!canCreateSettings) {
            toast.error('Nao tem permissão para criar tipos de contactos.');
            return;
        }

        setEditingType(null);
        setForm(emptyForm);
        setDialogOpen(true);
    };

    const openEdit = (type) => {
        if (!canUpdateSettings) {
            toast.error('Nao tem permissão para editar tipos de contactos.');
            return;
        }

        setEditingType(type);
        setForm({
            code: type.code || '',
            name: type.name || '',
            description: type.description || '',
            object_color_id: type.object_color_id ? String(type.object_color_id) : '',
            object_icon_id: type.object_icon_id ? String(type.object_icon_id) : '',
            status: String(type.status ?? 0),
        });
        setDialogOpen(true);
    };

    const handleSubmit = async () => {
        if (editingType && !canUpdateSettings) {
            toast.error('Nao tem permissão para editar tipos de contactos.');
            return;
        }

        if (!editingType && !canCreateSettings) {
            toast.error('Nao tem permissão para criar tipos de contactos.');
            return;
        }

        const code = form.code.trim();
        if (!code) {
            toast.error('Codigo e obrigatorio.');
            return;
        }

        if (!/^[a-z]+$/.test(code)) {
            toast.error('Codigo deve conter apenas letras minusculas.');
            return;
        }

        if (!form.name.trim()) {
            toast.error('Nome é obrigatório.');
            return;
        }

        setSubmitting(true);
        try {
            if (editingType) {
                const response = await api.post(externalContactTypeEndpoints.update, {
                    id: editingType.id,
                    uuid: editingType.uuid,
                    ...typePayload(form),
                });
                const updated = normalizeType(response.data?.data ?? response.data?.contact_type ?? response.data, {
                    ...editingType,
                    ...typePayload(form),
                });
                setTypes(prev => prev.map(type => type.id === editingType.id ? updated : type));
                toast.success(`Tipo "${updated.name}" atualizado.`);
            } else {
                const response = await api.post(externalContactTypeEndpoints.create, typePayload(form));
                const created = normalizeType(response.data?.data ?? response.data?.contact_type ?? response.data, {
                    id: Date.now(),
                    ...typePayload(form),
                });
                setTypes(prev => [created, ...prev]);
                toast.success(`Tipo "${created.name}" criado.`);
            }
            setDialogOpen(false);
        } catch (requestError) {
            toast.error(getApiErrorMessage(requestError, 'Não foi possível guardar o tipo de contacto externo.'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (type) => {
        if (!canDeleteSettings) {
            toast.error('Nao tem permissão para eliminar tipos de contactos.');
            return;
        }

        if (!window.confirm(`Eliminar tipo "${type.name}"?`)) return;

        setSubmitting(true);
        try {
            await api.post(externalContactTypeEndpoints.delete, {
                id: type.id,
                uuid: type.uuid,
            });
            setTypes(prev => prev.filter(item => item.id !== type.id));
            toast.success(`Tipo "${type.name}" removido.`);
        } catch (requestError) {
            toast.error(getApiErrorMessage(requestError, 'Não foi possível remover o tipo de contacto externo.'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-slate-900 text-3xl font-secondary font-bold tracking-tight">Tipos de Contactos</h1>
                        <p className="text-slate-600 font-primary mt-1">Gerir tipos usados para classificar contactos.</p>
                    </div>
                    {canCreateSettings && (
                        <Button onClick={openCreate} disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="create-external-contact-type">
                            <Plus size={18} className="mr-2"/>
                            Novo Tipo
                        </Button>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="p-4">
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-slate-600 font-medium">Total</p>
                            <UserCog size={18} className="text-blue-600"/>
                        </div>
                        <p className="text-2xl font-secondary font-bold text-slate-900 mt-1">{types.length}</p>
                    </Card>
                    <Card className="p-4">
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-slate-600 font-medium">Ativos</p>
                            <Badge className="bg-emerald-100 text-emerald-700 border-0">status 1</Badge>
                        </div>
                        <p className="text-2xl font-secondary font-bold text-slate-900 mt-1">{types.filter(type => Number(type.status) === 1).length}</p>
                    </Card>
                    <Card className="p-4">
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-slate-600 font-medium">Inativos</p>
                            <Badge className="bg-slate-100 text-slate-700 border-0">status 0</Badge>
                        </div>
                        <p className="text-2xl font-secondary font-bold text-slate-900 mt-1">{types.filter(type => Number(type.status) !== 1).length}</p>
                    </Card>
                </div>

                <div className="relative max-w-md">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                    <Input className="pl-9" value={search} onChange={event => setSearch(event.target.value)} placeholder="Pesquisar por codigo, nome, descricao ..." data-testid="external-contact-type-search"/>
                </div>

                <Card className="overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Codigo</TableHead>
                                <TableHead>Tipo</TableHead>
                                <TableHead>Cor</TableHead>
                                <TableHead>Ícone</TableHead>
                                <TableHead>Estado</TableHead>
                                {showActions && <TableHead className="text-right">Ações</TableHead>}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {(loading || error || filteredTypes.length === 0) ? (
                                <TableRow>
                                    <TableCell colSpan={showActions ? 6 : 5} className="text-center text-sm text-slate-400 italic py-10">
                                        {loading ? 'A carregar tipos...' : error || 'Sem tipos para mostrar.'}
                                    </TableCell>
                                </TableRow>
                            ) : filteredTypes.map(type => {
                                const color = colorById.get(String(type.object_color_id));
                                const icon = iconById.get(String(type.object_icon_id));
                                return (
                                    <TableRow key={type.id} data-testid={`external-contact-type-row-${type.id}`}>
                                        <TableCell>
                                            <code className="text-xs font-medium text-slate-700 bg-slate-100 px-2 py-1 rounded">
                                                {type.code || '-'}
                                            </code>
                                        </TableCell>
                                        <TableCell>
                                            <p className="font-medium text-slate-900">{type.name}</p>
                                            {type.description && <p className="text-xs text-slate-500 line-clamp-1">{type.description}</p>}
                                        </TableCell>
                                        <TableCell>
                                            {color ? (
                                                <span className="inline-flex items-center gap-2 text-sm text-slate-700">
                                                    <ColorOption color={color}/>
                                                </span>
                                            ) : <span className="text-xs text-slate-400 italic">Sem cor</span>}
                                        </TableCell>
                                        <TableCell>
                                            {icon ? (
                                                <span className="text-sm text-slate-700"><IconOption icon={icon}/></span>
                                            ) : <span className="text-xs text-slate-400 italic">Sem ícone</span>}
                                        </TableCell>
                                        <TableCell>
                                            <Badge className={Number(type.status) === 1 ? 'bg-emerald-100 text-emerald-700 border-0' : 'bg-slate-100 text-slate-700 border-0'}>
                                                {Number(type.status) === 1 ? 'Ativo' : 'Inativo'}
                                            </Badge>
                                        </TableCell>
                                        {showActions && (
                                            <TableCell className="text-right">
                                                {canUpdateSettings && (
                                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openEdit(type)} disabled={submitting} data-testid={`edit-external-contact-type-${type.id}`}>
                                                        <Edit2 size={14} className="text-slate-600"/>
                                                    </Button>
                                                )}
                                                {canDeleteSettings && (
                                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleDelete(type)} disabled={submitting} data-testid={`delete-external-contact-type-${type.id}`}>
                                                        <Trash2 size={14} className="text-red-500"/>
                                                    </Button>
                                                )}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Card>

                <Dialog open={dialogOpen && (editingType ? canUpdateSettings : canCreateSettings)} onOpenChange={(open) => setDialogOpen(open && (editingType ? canUpdateSettings : canCreateSettings))}>
                    <DialogContent className="max-w-xl" data-testid="external-contact-type-dialog">
                        <DialogHeader>
                            <DialogTitle>{editingType ? 'Editar Tipo de Contacto Externo' : 'Novo Tipo de Contacto Externo'}</DialogTitle>
                            <DialogDescription>Configurar codigo, nome, descricao, cor, icone e estado do tipo.</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-2">
                            <div className="space-y-2">
                                <Label>Codigo *</Label>
                                <Input
                                    value={form.code}
                                    onChange={event => setForm(prev => ({...prev, code: event.target.value.toLowerCase()}))}
                                    pattern="[a-z]+"
                                    data-testid="external-contact-type-code"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Nome *</Label>
                                <Input value={form.name} onChange={event => setForm(prev => ({...prev, name: event.target.value}))} data-testid="external-contact-type-name"/>
                            </div>
                            <div className="space-y-2">
                                <Label>Descrição</Label>
                                <Textarea rows={3} value={form.description} onChange={event => setForm(prev => ({...prev, description: event.target.value}))} data-testid="external-contact-type-description"/>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-1"><Palette size={13}/> Cor</Label>
                                    <Select value={form.object_color_id || 'none'} onValueChange={value => setForm(prev => ({...prev, object_color_id: value === 'none' ? '' : value}))}>
                                        <SelectTrigger data-testid="external-contact-type-color">
                                            <SelectValue>
                                                {form.object_color_id
                                                    ? <ColorOption color={colorById.get(String(form.object_color_id))}/>
                                                    : 'Sem cor'}
                                            </SelectValue>
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Sem cor</SelectItem>
                                            {colors.map(color => (
                                                <SelectItem key={color.id} value={String(color.id)}>
                                                    <ColorOption color={color}/>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-1"><Shapes size={13}/> Ícone</Label>
                                    <Select value={form.object_icon_id || 'none'} onValueChange={value => setForm(prev => ({...prev, object_icon_id: value === 'none' ? '' : value}))}>
                                        <SelectTrigger data-testid="external-contact-type-icon">
                                            <SelectValue>
                                                {form.object_icon_id
                                                    ? <IconOption icon={iconById.get(String(form.object_icon_id))}/>
                                                    : 'Sem ícone'}
                                            </SelectValue>
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Sem ícone</SelectItem>
                                            {icons.map(icon => (
                                                <SelectItem key={icon.id} value={String(icon.id)}>
                                                    <IconOption icon={icon}/>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Estado</Label>
                                <Select value={form.status} onValueChange={value => setForm(prev => ({...prev, status: value}))}>
                                    <SelectTrigger data-testid="external-contact-type-status"><SelectValue/></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="1">Ativo</SelectItem>
                                        <SelectItem value="0">Inativo</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={submitting}>Cancelar</Button>
                            <Button onClick={handleSubmit} disabled={submitting || (editingType ? !canUpdateSettings : !canCreateSettings)} className="bg-blue-600 hover:bg-blue-700" data-testid="external-contact-type-submit">
                                {submitting ? 'A guardar...' : editingType ? 'Guardar' : 'Criar Tipo'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
};
